import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Ban, CheckCircle2, Clock, Loader2, Pencil, MessageCircle, XCircle } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Chip } from '@/components/ui/Chip';
import { InvoiceTemplate } from '@/components/invoice/InvoiceTemplate';
import { PdfActions } from '@/components/invoice/PdfActions';
import { EditTransactionDialog } from '@/components/transactions/EditTransactionDialog';
import { VoidTransactionDialog } from '@/components/transactions/VoidTransactionDialog';
import { PaymentProofsCard } from '@/components/invoice/PaymentProofsCard';
import { formatDateTime } from '@/lib/format';
import { useTransaction, useUpdateFulfillment } from '@/hooks/useTransactions';
import { useAuth } from '@/hooks/useAuth';
import { isOwner } from '@/lib/permissions';
import { usePermissions } from '@/hooks/usePermissions';
import { useRequestsFor } from '@/hooks/useApprovals';
import { supabase } from '@/lib/supabase';
import { mapEvent } from '@/lib/api/mappers';
import { resendWhatsApp } from '@/lib/api/invoice';
import type { CompanyInfo, AppEvent } from '@/types/domain';


export default function Invoice() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: transaction, isLoading, error } = useTransaction(id ?? null);
  const { user } = useAuth();
  const { can } = usePermissions();
  const updateFulfillment = useUpdateFulfillment();
  const canEdit = can('transactions', 'edit');
  const canVoidDirect = can('transactions', 'cancel');
  // Proof deletion is still an owner-only policy in the database.
  const canDeleteProofs = isOwner(user?.role);
  // The latest void request for this invoice, if a cashier filed one.
  const { data: voidRequests } = useRequestsFor('transaction.void', 'transaction_id', id);
  const voidRequest = voidRequests?.[0] ?? null;

  const [company, setCompany] = useState<CompanyInfo | null>(null);
  const [event, setEvent] = useState<AppEvent | null>(null);
  const [fulfillError, setFulfillError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendSuccess, setSendSuccess] = useState(false);

  const handleMarkPaid = async () => {
    if (!transaction) return;
    setFulfillError(null);
    try {
      await updateFulfillment.mutateAsync({
        id: transaction.id,
        input: { status: 'paid', notes: 'Marked paid from invoice view' },
      });
    } catch (err) {
      setFulfillError(err instanceof Error ? err.message : 'Gagal update status');
    }
  };

  const handleSendWhatsApp = async () => {
    if (!transaction) return;
    setSendError(null);
    setSendSuccess(false);
    setSendLoading(true);
    try {
      const res = await resendWhatsApp(transaction.id);
      if (res.status !== 'sent') {
        throw new Error(res.error ?? `Gateway ${res.gateway ?? '-'}: ${res.status}`);
      }
      setSendSuccess(true);
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Gagal kirim WhatsApp');
    } finally {
      setSendLoading(false);
    }
  };

  useEffect(() => {
    if (!transaction) return;
    // Fetch company + event in parallel
    Promise.all([
      supabase.from('company_info').select('*').eq('id', true).maybeSingle(),
      supabase.from('events').select('*').eq('id', transaction.eventId).maybeSingle(),
    ]).then(([companyRes, eventRes]) => {
      if (companyRes.data) {
        setCompany({
          ...(companyRes.data as unknown as CompanyInfo),
          logoUrl: companyRes.data.logo_url ?? undefined,
          defaultTaxRate: Number(companyRes.data.default_tax_rate),
        } as CompanyInfo);
      }
      if (eventRes.data) setEvent(mapEvent(eventRes.data));
    });
  }, [transaction]);

  if (!id) {
    return (
      <>
        <PageHeader title="Invoice" />
        <Card>
          <p className="text-body text-ink-subtle">No transaction ID provided.</p>
          <Button variant="secondary" onClick={() => navigate('/dashboard')} className="mt-4">
            Back to dashboard
          </Button>
        </Card>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <PageHeader title="Invoice" />
        <Card>
          <p className="text-body text-ink-subtle">Loading invoice…</p>
        </Card>
      </>
    );
  }

  if (error || !transaction) {
    return (
      <>
        <PageHeader title="Invoice not found" />
        <Card>
          <div className="flex items-start gap-3">
            <AlertCircle size={20} className="text-danger flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-body text-ink">
                We could not find this invoice. It may have been deleted or the link is wrong.
              </p>
              <p className="text-caption text-ink-subtle mt-1">
                ID: <code className="font-mono">{id}</code>
              </p>
            </div>
          </div>
          <Button
            variant="secondary"
            onClick={() => navigate('/dashboard')}
            className="mt-4"
            leftIcon={<ArrowLeft size={16} />}
          >
            Back to dashboard
          </Button>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        overline={transaction.invoiceNumber}
        title="Invoice"
        description="View, print, atau share invoice ini ke customer."
        actions={
          <div className="flex items-center gap-2">
            {transaction.openclawStatus && (
              <Chip
                variant={
                  transaction.openclawStatus === 'sent'
                    ? 'success'
                    : transaction.openclawStatus === 'failed'
                      ? 'danger'
                      : 'default'
                }
              >
                WhatsApp: {transaction.openclawStatus}
              </Chip>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate(-1)}
              leftIcon={<ArrowLeft size={16} />}
            >
              Back
            </Button>
            {canEdit && transaction && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditOpen(true)}
                leftIcon={<Pencil size={14} />}
              >
                Edit
              </Button>
            )}
            {/* Any staff may START a void; the database decides whether it
                goes through (owner directly, cashier with the owner's PIN). */}
            {transaction.paymentStatus !== 'cancelled' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setVoidOpen(true)}
                leftIcon={<Ban size={14} className="text-danger" />}
                className="text-danger"
              >
                Void
              </Button>
            )}
          </div>
        }
      />

      {transaction.paymentStatus === 'cancelled' && (
        <div
          role="status"
          className="mb-4 p-4 rounded-lg border border-danger/30 bg-danger/5 flex items-start gap-3"
        >
          <Ban size={18} className="text-danger shrink-0 mt-0.5" />
          <div className="text-small">
            <p className="font-semibold text-danger">Transaksi ini sudah di-void</p>
            <p className="text-ink-subtle mt-0.5">
              {transaction.voidReason ? `"${transaction.voidReason}"` : 'Tanpa alasan tercatat'}
              {transaction.voidedByName ? ` — oleh ${transaction.voidedByName}` : ''}
              {transaction.voidApprovedByName && transaction.voidApprovedByName !== transaction.voidedByName
                ? `, disetujui ${transaction.voidApprovedByName}`
                : ''}
              {transaction.voidedAt ? ` · ${formatDateTime(transaction.voidedAt)}` : ''}.
              Stok sudah kembali ke lot asalnya; transaksi ini tidak dihitung di laporan.
            </p>
          </div>
        </div>
      )}

      {transaction.paymentStatus !== 'cancelled' && voidRequest && voidRequest.status !== 'cancelled' && (
        <div
          role="status"
          className={
            voidRequest.status === 'approved' && !voidRequest.consumedAt
              ? 'mb-4 p-3 rounded-lg border border-success/30 bg-success/5 flex items-start gap-3 text-small'
              : voidRequest.status === 'rejected'
                ? 'mb-4 p-3 rounded-lg border border-line bg-canvas flex items-start gap-3 text-small'
                : 'mb-4 p-3 rounded-lg border border-warning/30 bg-warning/5 flex items-start gap-3 text-small'
          }
        >
          {voidRequest.status === 'approved' ? (
            <CheckCircle2 size={18} className="text-success shrink-0 mt-0.5" />
          ) : voidRequest.status === 'rejected' ? (
            <XCircle size={18} className="text-ink-subtle shrink-0 mt-0.5" />
          ) : (
            <Clock size={18} className="text-warning shrink-0 mt-0.5" />
          )}
          <div>
            {voidRequest.status === 'pending' && (
              <>
                <p className="font-semibold text-ink">Permintaan void menunggu persetujuan</p>
                <p className="text-ink-subtle mt-0.5">
                  Diajukan {voidRequest.requestedByName ?? 'kasir'} · {formatDateTime(voidRequest.createdAt)}
                  {voidRequest.reason ? ` · “${voidRequest.reason}”` : ''}. Butuh approver level {voidRequest.requiredLevel}.
                </p>
              </>
            )}
            {voidRequest.status === 'approved' && !voidRequest.consumedAt && (
              <>
                <p className="font-semibold text-success">Void sudah disetujui</p>
                <p className="text-ink-subtle mt-0.5">
                  Klik <b>Void</b> untuk menjalankannya. Persetujuan berlaku untuk invoice ini saja dan dipakai sekali.
                </p>
              </>
            )}
            {voidRequest.status === 'rejected' && (
              <>
                <p className="font-semibold text-ink">Permintaan void ditolak</p>
                <p className="text-ink-subtle mt-0.5">
                  {voidRequest.resolvedAt ? formatDateTime(voidRequest.resolvedAt) : ''}. Ajukan lagi dengan alasan yang lebih jelas, atau minta owner mengetik PIN.
                </p>
              </>
            )}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <InvoiceTemplate
          transaction={transaction}
          company={company}
          event={event}
        />

        <aside className="space-y-4 print:hidden">
          <Card>
            <h3 className="text-subhead font-display font-semibold text-ink mb-1">
              Aksi
            </h3>
            <p className="text-caption text-ink-subtle mb-4">
              Download PDF untuk arsip, atau share langsung ke customer.
            </p>
            <PdfActions transaction={transaction} />

            {/* WhatsApp link send — owner/cashier can resend */}
            <div className="mt-4 pt-4 border-t border-line space-y-2">
              <Button
                variant="secondary"
                size="md"
                fullWidth
                onClick={handleSendWhatsApp}
                loading={sendLoading}
                leftIcon={
                  sendLoading ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : sendSuccess ? (
                    <CheckCircle2 size={16} />
                  ) : (
                    <MessageCircle size={16} />
                  )
                }
                disabled={!transaction.customerSnapshot?.phone}
              >
                {sendSuccess ? 'Terkirim!' : 'Kirim WhatsApp link'}
              </Button>
              {sendError && (
                <p className="text-caption text-danger">{sendError}</p>
              )}
              {sendSuccess && (
                <p className="text-caption text-success">
                  Link invoice sudah dikirim ke WhatsApp customer.
                </p>
              )}
              {!transaction.customerSnapshot?.phone && (
                <p className="text-caption text-ink-subtle">
                  Tidak ada nomor HP customer — tidak bisa kirim WhatsApp.
                </p>
              )}
            </div>
          </Card>

          <PaymentProofsCard transaction={transaction} canDelete={canDeleteProofs} />

          <Card>
            <h3 className="text-subhead font-display font-semibold text-ink mb-1">
              Status
            </h3>
            <dl className="space-y-2 mt-3 text-small">
              <DefRow label="Status">
                <Chip
                  variant={
                    transaction.paymentStatus === 'paid'
                      ? 'success'
                      : transaction.paymentStatus === 'pending_delivery'
                        ? 'warning'
                        : transaction.paymentStatus === 'cancelled'
                          ? 'danger'
                          : 'default'
                  }
                >
                  {transaction.paymentStatus === 'cancelled' ? 'VOID' : transaction.paymentStatus.replace('_', ' ')}
                </Chip>
              </DefRow>
              <DefRow label="Fulfillment">
                <Chip variant={transaction.fulfillment === 'now' ? 'success' : 'warning'}>
                  {transaction.fulfillment === 'now' ? 'Ambil sekarang' : 'Kirim nanti'}
                </Chip>
              </DefRow>
              <DefRow label="Metode">
                <span className="font-medium text-ink uppercase">
                  {transaction.paymentMethod}
                </span>
              </DefRow>
              {transaction.fulfillment === 'later' && transaction.paymentStatus === 'pending_delivery' && (
                <p className="text-caption text-warning mt-2">
                  ⚠ Stok belum dikurangi. Akan dipotong pas barang dikirim.
                </p>
              )}
            </dl>

            {/* Mark as paid — only for pending_delivery orders */}
            {transaction.paymentStatus === 'pending_delivery' && (
              <div className="mt-4 pt-4 border-t border-line space-y-2">
                <Button
                  variant="primary"
                  size="md"
                  fullWidth
                  onClick={handleMarkPaid}
                  loading={updateFulfillment.isPending}
                  leftIcon={
                    updateFulfillment.isPending ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <CheckCircle2 size={16} />
                    )
                  }
                >
                  Tandai sudah dibayar
                </Button>
                {fulfillError && (
                  <p className="text-caption text-danger">{fulfillError}</p>
                )}
              </div>
            )}
          </Card>
        </aside>
      </div>

      {transaction && (
        <EditTransactionDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          transaction={transaction}
        />
      )}
      {transaction && (
        <VoidTransactionDialog
          open={voidOpen}
          onClose={() => setVoidOpen(false)}
          transaction={transaction}
          canDirect={canVoidDirect}
          request={voidRequest}
        />
      )}
    </>
  );
}

function DefRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-caption text-ink-subtle">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}
